import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Search box + column visibility + density, all built into MUI's DataGrid
const CustomToolbar = () => (
    <GridToolbarContainer className="p-3 flex flex-wrap gap-2 items-center justify-between border-b">
        <div className="flex gap-1">
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
        </div>
        <GridToolbarQuickFilter placeholder="Search brands..." />
    </GridToolbarContainer>
);

const BrandTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [brands, setBrands] = useState([]);
    const [pageSize, setPageSize] = useState(25);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.brands || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/api/v1/admin/brands');
            setBrands(data.brands);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load brands", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/brand/${id}`);
            enqueueSnackbar("Brand deleted", { variant: "success" });
            setBrands((prev) => prev.filter((b) => b._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete brand", { variant: "error" });
        }
    };

    const columns = [
        { field: "name", headerName: "Brand Name", minWidth: 220, flex: 1 },
        {
            field: "productCount",
            headerName: "Products",
            type: "number",
            minWidth: 120,
            flex: 0.4,
            renderCell: (params) => (
                <span>{params.row.productCount} product{params.row.productCount !== 1 ? "s" : ""}</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 110,
            flex: 0.3,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Actions editRoute={"brand"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.name} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = brands.map((b) => ({
        id: b._id,
        name: b.name,
        productCount: b.productCount,
    }));

    return (
        <>
            <MetaData title="Admin: Brands | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Brands</h1>
                {canCreate && (
                    <Link to="/admin/brand/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Brand</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">
                <div style={{ height: 600 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        disableSelectIconOnClick
                        rowHeight={64}
                        pageSize={pageSize}
                        onPageSizeChange={(newSize) => setPageSize(newSize)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        components={{ Toolbar: CustomToolbar }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f9fafb', fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280' },
                            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default BrandTable;